import { useId } from 'react';

import {
  attemptMeaningText,
  attemptSelectOptions,
  attemptSourceText,
  type AttemptField,
  type AttemptSource,
} from './attemptSettings';
import styles from './AttemptsField.module.css';

interface AttemptsFieldProps {
  field: AttemptField;
  // The value the dialog opened with, and where it came from.
  initialValue: number;
  initialSource: AttemptSource;
  label: string;
  // The dialog's own style for a label around its select.
  labelClassName: string;
  level: 'chapter' | 'course';
  onChange(value: number): void;
  value: number;
}

// An attempts select for the Question Settings dialogs, with a line saying
// where the value comes from and what it means for students (issue #54).
// Unlimited is -1; the line is the select's description, and it is read out
// when it changes.
export function AttemptsField({
  field,
  initialSource,
  initialValue,
  label,
  labelClassName,
  level,
  onChange,
  value,
}: AttemptsFieldProps) {
  const hintId = useId();
  // A changed value is saved on this chapter, or this course.
  const source = value === initialValue ? initialSource : level;
  const meaning = attemptMeaningText(field, value);

  return (
    <div className={styles.field}>
      <label className={labelClassName}>
        <span>{label}</span>
        <select
          aria-describedby={hintId}
          onChange={(event) => onChange(Number(event.target.value))}
          value={value}
        >
          {/* From the value the dialog opened with, so a stored value outside
              the list stays choosable after another is picked. */}
          {attemptSelectOptions(initialValue).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <p aria-live="polite" className={styles.hint} id={hintId}>
        {attemptSourceText(source, level)}
        {meaning ? ` ${meaning}` : ''}
      </p>
    </div>
  );
}
